import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, Bars3Icon, XMarkIcon, BellIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import type { Notification, User } from '../../../types';
import React from "react";

interface NavbarProps {
  user: User;
  notifications: Notification[];
  isUserMenuOpen: boolean;
  setIsUserMenuOpen: (open: boolean) => void;
  isNotificationsOpen: boolean;
  setIsNotificationsOpen: (open: boolean) => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  userMenuRef: React.RefObject<HTMLDivElement | null>;
  notificationsRef: React.RefObject<HTMLDivElement | null>;
  handleLogout: () => void;
  onMarkAsRead?: (notificationId: number) => void;
}

function Navbar({
  user,
  notifications,
  isUserMenuOpen,
  setIsUserMenuOpen,
  isNotificationsOpen,
  setIsNotificationsOpen,
  isSidebarOpen,
  setIsSidebarOpen,
  userMenuRef,
  notificationsRef,
  handleLogout,
  onMarkAsRead,
}: NavbarProps) {
  const navigate = useNavigate();

  const markAsRead = async (notificationId: number) => {
    try {
      const response = await axiosClient.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;

      await axiosClient.post(
        `/elearning/mark_notification_read/${notificationId}/`,
        {},
        {
          withCredentials: true,
          headers: { 'X-CSRFToken': csrfToken },
        }
      );

      if (onMarkAsRead) onMarkAsRead(notificationId);
    } catch (error) {
      console.error('[Navbar] Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) markAsRead(notification.id);

    if (notification.related_object_url && notification.related_object_url !== '#') {
      const correctedUrl = notification.related_object_url.replace('/course/', '/courses/');
      if (correctedUrl.startsWith('/courses/') || correctedUrl === '/profile') {
        navigate(correctedUrl);
      } else {
        navigate('/courses');
      }
    } else if (notification.related_object_type === 'Course' && notification.related_object_id !== null) {
      navigate(`/courses/${notification.related_object_id}`);
    } else if (notification.related_object_type === 'Deadline' && notification.related_object_id !== null) {
      axiosClient.get(`/elearning/api/deadlines/${notification.related_object_id}/`, { withCredentials: true })
        .then((response: { data: { course: { id: any; }; }; }) => {
          const courseId = response.data.course.id;
          navigate(`/courses/${courseId}`);
        })
        .catch((error: any) => {
          console.error(`[Navbar] Error fetching deadline course: ${error}`);
          navigate('/courses');
        });
    } else if (notification.title.includes('Profile')) {
      navigate('/profile');
    } else {
      navigate('/courses');
    }
  };

  return (
  <nav className="bg-white shadow sticky top-0 z-50">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">

      {/* Left: Logo & main links */}
      <div className="flex items-center">
        <Link to="/" className="text-2xl font-extrabold text-blue-700 tracking-tight hover:text-blue-800 transition-colors duration-200">
          LearnSphere
        </Link>
        <div className="hidden md:flex ml-10 space-x-8">
          <Link to="/courses" className="text-gray-600 hover:text-blue-700 font-medium transition-colors duration-200">
            Courses
          </Link>
          {(user.role === 'teacher' || user.role === 'student') && (
            <Link to="/search-users" className="text-gray-600 hover:text-blue-700 font-medium transition-colors duration-200">
              Search Users
            </Link>
          )}
          {user.role === 'teacher' && (
            <Link to="/courses/create" className="text-gray-600 hover:text-blue-700 font-medium transition-colors duration-200">
              Create Course
            </Link>
          )}
          <Link to="/chat" className="text-gray-600 hover:text-blue-700 font-medium transition-colors duration-200">
            Chat
          </Link>
        </div>
      </div>

      {/* Right: Notifications, User menu, Mobile toggle */}
      <div className="flex items-center space-x-4">
        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <button
            className="flex items-center text-gray-700 hover:text-blue-700 relative p-2 rounded-full transition-all duration-200"
            aria-label="View notifications"
            onClick={() => {
              setIsNotificationsOpen(!isNotificationsOpen);
              setIsUserMenuOpen(false);
            }}
            aria-expanded={isNotificationsOpen}
          >
            <BellIcon className="h-6 w-6" />
            {notifications.some(n => !n.is_read) && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {notifications.filter(n => !n.is_read).length}
              </span>
            )}
          </button>

          <div
            className={`absolute right-0 mt-2 w-80 bg-white shadow-xl rounded-lg py-2 z-10 max-h-96 overflow-y-auto transition-all duration-200 ${
              isNotificationsOpen ? 'block' : 'hidden'
            }`}
          >
            {notifications.length ? (
              <ul className="space-y-2">
                {notifications.map(notif => (
                  <li
                    key={notif.id}
                    className={`px-4 py-3 rounded cursor-pointer transition-colors duration-200 ${
                      notif.is_read ? 'bg-gray-100' : 'bg-blue-100 font-bold'
                    }`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <p className="text-gray-700 text-sm font-medium">{notif.title}</p>
                    <p className="text-gray-500 text-xs line-clamp-2">{notif.message}</p>
                    <p className="text-gray-400 text-xs">
                      {new Date(notif.timestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-3 text-gray-500 text-sm text-center">No notifications</p>
            )}
          </div>
        </div>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            className="flex items-center text-gray-700 hover:text-blue-700 font-medium transition-colors duration-200 p-2 rounded-lg"
            onClick={() => {
              setIsUserMenuOpen(!isUserMenuOpen);
              setIsNotificationsOpen(false);
            }}
            aria-expanded={isUserMenuOpen}
          >
            {user.real_name || user.username}
            <ChevronDownIcon className="h-5 w-5 ml-2" />
          </button>
          <div
            className={`absolute right-0 mt-2 w-48 bg-white shadow-xl rounded-lg py-2 transition-all duration-200 ${
              isUserMenuOpen ? 'block' : 'hidden'
            }`}
          >
            <Link
              to="/profile"
              className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium transition-colors duration-200"
            >
              Profile
            </Link>
            <button
              onClick={handleLogout}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile sidebar toggle */}
        <button
          className="md:hidden text-gray-600 hover:text-blue-700 p-2 rounded transition-all duration-200"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {isSidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
      </div>
    </div>
  </nav>
);

}

export default Navbar;
